import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Image, Modal, Table, message } from 'antd';
import { formatTs } from './voucherReminderUtils';
import {
  AppliedVoucherRow,
  firstImage,
  loadAppliedVoucherRows,
} from './voucherReminderAppliedVouchers';

export interface VoucherReminderAppliedVouchersModalProps {
  visible: boolean;
  onClose: () => void;
  voucherIds: unknown;
  pageInfo?: any;
  title?: string;
}

const VoucherReminderAppliedVouchersModal = ({
  visible,
  onClose,
  voucherIds,
  pageInfo,
  title,
}: VoucherReminderAppliedVouchersModalProps) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, any>) =>
    intl.formatMessage({ id }, values);
  const empty = t('voucherReminder.empty');

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AppliedVoucherRow[]>([]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setLoading(true);
    loadAppliedVoucherRows({
      voucherIds,
      pageInfo,
      locale: intl.locale,
    })
      .then((list) => {
        if (!cancelled) setRows(list);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) {
          message.error(t('voucherReminder.voucherPicker.loadFail'));
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, voucherIds, pageInfo, intl.locale]);

  return (
    <Modal
      title={title || t('voucherReminder.detail.voucherListTitle')}
      visible={visible}
      onCancel={onClose}
      footer={
        <Button onClick={onClose}>{t('voucherReminder.action.close')}</Button>
      }
      width={860}
      destroyOnClose
      centered
    >
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        pagination={false}
        scroll={{ y: 420 }}
        columns={[
          {
            title: t('voucherReminder.voucherPicker.col.image'),
            dataIndex: 'images',
            width: 80,
            render: (images: any) => {
              const src = firstImage(images);
              return src ? (
                <Image
                  src={src}
                  width={48}
                  height={48}
                  style={{ objectFit: 'cover' }}
                />
              ) : (
                empty
              );
            },
          },
          {
            title: t('voucherReminder.col.id'),
            dataIndex: 'id',
            width: 80,
          },
          {
            title: t('voucherReminder.voucherPicker.col.name'),
            dataIndex: 'name',
            ellipsis: true,
            render: (v: string, row: AppliedVoucherRow) => v || `#${row.id}`,
          },
          {
            title: t('voucherReminder.voucherPicker.col.partner'),
            dataIndex: 'partnerName',
            width: 160,
            ellipsis: true,
            render: (v: string) => v || empty,
          },
          {
            title: t('voucherReminder.voucherPicker.col.period'),
            dataIndex: 'startDate',
            width: 200,
            render: (_: any, row: AppliedVoucherRow) =>
              `${formatTs(row.startDate, intl.locale)} → ${formatTs(
                row.endDate,
                intl.locale
              )}`,
          },
        ]}
      />
    </Modal>
  );
};

export default VoucherReminderAppliedVouchersModal;
